import * as React from 'react';

import { Botao } from '@/components/ui/button';
import { Entrada, Selecao } from '@/components/ui/input';
import {
  LIMITES,
  PRESETS,
  dataDigitadaValida,
  presetAtivo,
  temFiltroAlemDoPeriodo,
  type FiltrosRelatorio,
} from '@/features/relatorios/filtros';
import {
  PRIORIDADES,
  ROTULOS_PRIORIDADE,
  ROTULOS_TIPO,
  TIPOS_SOLICITACAO,
} from '@/features/solicitacoes/status';
import { ORIGENS, ROTULOS_ORIGEM } from '@/features/solicitacoes/origem';
import type { Especialidade, UbsResumida } from '@/features/admin/tipos';

interface Props {
  filtros: FiltrosRelatorio;
  unidades: UbsResumida[] | undefined;
  especialidades: Pick<Especialidade, 'id' | 'nome'>[] | undefined;
  aoMudar: (chave: string, valor: string) => void;
  aoDefinirPeriodo: (de: string, ate: string) => void;
  aoLimpar: () => void;
}

const ID_AVISO_PERIODO = 'aviso-periodo-relatorio';

/**
 * Campo de data do período, com rascunho próprio.
 *
 * Ligar o campo direto à URL impedia a digitação: enquanto a data está pela
 * metade o navegador entrega valor vazio, o vazio apagava o parâmetro, a tela
 * voltava ao período padrão e o campo era reescrito por cima do que se estava
 * digitando. O rascunho segura a digitação, e só a data completa vai para a
 * URL. Ao sair do campo sem completar, volta a data que está valendo.
 */
function CampoData({
  rotulo,
  valor,
  min,
  max,
  invertido,
  aoConfirmar,
}: {
  rotulo: string;
  valor: string;
  min?: string;
  max?: string;
  invertido: boolean;
  aoConfirmar: (valor: string) => void;
}) {
  const [rascunho, setRascunho] = React.useState(valor);

  // Atalho de período, "voltar" do navegador ou link aberto: a URL manda.
  React.useEffect(() => setRascunho(valor), [valor]);

  return (
    <label className="flex flex-col gap-1 text-xs font-medium">
      {rotulo}
      <Entrada
        type="date"
        value={rascunho}
        min={min}
        max={max}
        aria-invalid={invertido || undefined}
        aria-describedby={invertido ? ID_AVISO_PERIODO : undefined}
        onChange={(evento) => {
          const digitado = evento.target.value;
          setRascunho(digitado);
          if (dataDigitadaValida(digitado) && digitado !== valor) aoConfirmar(digitado);
        }}
        onBlur={() => {
          if (!dataDigitadaValida(rascunho)) setRascunho(valor);
        }}
        className="h-11 w-full min-w-36 sm:h-9 sm:w-40"
      />
    </label>
  );
}

/**
 * Barra de filtros dos relatórios.
 *
 * O período fica primeiro e sempre visível: os demais filtros recortam o que
 * já foi recortado por ele, e um número sem período é um número sem
 * significado.
 */
export function FiltrosRelatorio({
  filtros,
  unidades,
  especialidades,
  aoMudar,
  aoDefinirPeriodo,
  aoLimpar,
}: Props) {
  const preset = presetAtivo(filtros);
  const periodoInvertido = filtros.de > filtros.ate;

  return (
    <div className="border-borda bg-cartao sem-impressao mb-5 flex flex-col gap-3 rounded-xl border p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap gap-1" role="group" aria-label="Atalhos de período">
          {PRESETS.map((atalho) => {
            const intervalo = atalho.calcular(new Date());
            return (
              <Botao
                key={atalho.chave}
                tamanho="sm"
                variante={preset === atalho.chave ? 'primario' : 'contorno'}
                aria-pressed={preset === atalho.chave}
                onClick={() => aoDefinirPeriodo(intervalo.de, intervalo.ate)}
              >
                {atalho.rotulo}
              </Botao>
            );
          })}
        </div>

        <div className="flex w-full flex-col gap-1 sm:w-auto">
          <div className="flex w-full items-end gap-2">
            <CampoData
              rotulo="De"
              valor={filtros.de}
              max={filtros.ate}
              invertido={periodoInvertido}
              aoConfirmar={(valor) => aoMudar('de', valor)}
            />
            <CampoData
              rotulo="Até"
              valor={filtros.ate}
              min={filtros.de}
              invertido={periodoInvertido}
              aoConfirmar={(valor) => aoMudar('ate', valor)}
            />
          </div>
          {periodoInvertido && (
            <p id={ID_AVISO_PERIODO} role="alert" className="text-destrutivo text-xs">
              A data inicial é posterior à final.
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Selecao
          value={filtros.ubsId}
          onChange={(evento) => aoMudar('ubs', evento.target.value)}
          aria-label="Filtrar por UBS"
          className="h-11 w-full sm:h-9 sm:w-52"
        >
          <option value="">Todas as UBS</option>
          {unidades?.map((unidade) => (
            <option key={unidade.id} value={unidade.id}>
              {unidade.nome}
            </option>
          ))}
        </Selecao>

        <Selecao
          value={filtros.especialidadeId}
          onChange={(evento) => aoMudar('especialidade', evento.target.value)}
          aria-label="Filtrar por especialidade"
          className="h-11 w-full sm:h-9 sm:w-52"
        >
          <option value="">Todas as especialidades</option>
          {especialidades?.map((especialidade) => (
            <option key={especialidade.id} value={especialidade.id}>
              {especialidade.nome}
            </option>
          ))}
        </Selecao>

        <Selecao
          value={filtros.tipo}
          onChange={(evento) => aoMudar('tipo', evento.target.value)}
          aria-label="Filtrar por tipo"
          className="h-11 w-full sm:h-9 sm:w-40"
        >
          <option value="">Todos os tipos</option>
          {TIPOS_SOLICITACAO.map((tipo) => (
            <option key={tipo} value={tipo}>
              {ROTULOS_TIPO[tipo]}
            </option>
          ))}
        </Selecao>

        <Selecao
          value={filtros.prioridade}
          onChange={(evento) => aoMudar('prioridade', evento.target.value)}
          aria-label="Filtrar por prioridade"
          className="h-11 w-full sm:h-9 sm:w-40"
        >
          <option value="">Todas as prioridades</option>
          {PRIORIDADES.map((prioridade) => (
            <option key={prioridade} value={prioridade}>
              {ROTULOS_PRIORIDADE[prioridade]}
            </option>
          ))}
        </Selecao>

        <Selecao
          value={filtros.origem}
          onChange={(evento) => aoMudar('origem', evento.target.value)}
          aria-label="Filtrar por origem"
          className="h-11 w-full sm:h-9 sm:w-48"
        >
          <option value="">Todas as origens</option>
          {ORIGENS.map((origem) => (
            <option key={origem} value={origem}>
              {ROTULOS_ORIGEM[origem]}
            </option>
          ))}
        </Selecao>

        <Selecao
          value={filtros.limite}
          onChange={(evento) => aoMudar('limite', evento.target.value)}
          aria-label="Tamanho dos rankings"
          className="h-11 w-full sm:h-9 sm:w-44"
        >
          {LIMITES.map((limite) => (
            <option key={limite} value={limite}>
              Rankings com {limite}
            </option>
          ))}
        </Selecao>

        {temFiltroAlemDoPeriodo(filtros) && (
          <Botao variante="fantasma" tamanho="sm" onClick={aoLimpar}>
            Limpar filtros
          </Botao>
        )}
      </div>
    </div>
  );
}
